import React, { useState, useEffect } from "react";
import { C, fmt, text } from "../theme";
import { usePermissions } from "../PermissionContext";
import WatcherPanel from "../components/WatcherPanel";
import DocumentSection from "../components/DocumentSection";

import { Card, Btn, Notice, StatusPill as Badge, ApprovalTrail, ReturnedBanner, rowAction } from "../components/ui";

export default function VirementPage({ initialId }) {
  const { me } = usePermissions();
  const [virements, setVirements] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [aors, setAors] = useState([]);
  const [sel, setSel] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);

  // Form
  const [fromRef, setFromRef] = useState("");
  const [toRef, setToRef] = useState("");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [linkedAOR, setLinkedAOR] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState(null); // { kind: "success" | "error", text }

  const loadList = () => {
    setLoading(true);
    Promise.all([
      fetch("/api/virements").then(r => r.json()),
      fetch("/api/budgets").then(r => r.json()),
      fetch("/api/aors").then(r => r.json()),
    ]).then(([v, b, p]) => {
      setVirements(v); setBudgets(b); setAors(p);
    }).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(loadList, []);

  const loadDetail = (id) => {
    setSel(id);
    fetch("/api/virements/" + id).then(r => r.json()).then(setDetail).catch(console.error);
  };
  // Deep link from the Approvals card: open that virement on mount.
  useEffect(() => { if (initialId) loadDetail(initialId); }, [initialId]); // eslint-disable-line react-hooks/exhaustive-deps

  const overBudgetAORs = aors.filter(p => p.budgetCheck?.status === "OVER_BUDGET" && !p.linkedVirementId);
  // The budget lines an AOR is short on. A split-funded AOR is checked line by
  // line, so the virement goes to the line that cannot cover its share — which
  // need not be the header budget — and there may be more than one.
  const shortLines = (aor) => (aor.budgetChecks && aor.budgetChecks.length ? aor.budgetChecks : (aor.budgetCheck ? [aor.budgetCheck] : []))
    .filter(c => c.status === "OVER_BUDGET");
  const shortfallText = (aor) => shortLines(aor).map(c => `${c.budgetRef} short by ${fmt(Math.abs(c.after || 0))}`).join("; ") || aor.budgetRef;
  const fromBudget = budgets.find(b => b.ref === fromRef);
  const toBudget = budgets.find(b => b.ref === toRef);

  const handleCreate = async () => {
    if (!fromRef || !toRef || !amount || !reason) {
      setNotice({ kind: "error", text: "Please fill in all required fields." }); return;
    }
    setSubmitting(true);
    try {
      const aor = aors[0];
      const res = await fetch("/api/virements", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fromRef, toRef, amount: parseFloat(amount), reason,
          requestorId: aor?.requestorId || "system",
          linkedAORId: linkedAOR || undefined,
        }),
      });
      const data = await res.json();
      // On failure the form stays mounted with its input intact.
      if (!res.ok) { setNotice({ kind: "error", text: "Error: " + (data.error || "Failed") }); return; }
      setNotice({ kind: "success", text: "Virement Created: " + data.virNumber });
      setShowForm(false);
      setFromRef(""); setToRef(""); setAmount(""); setReason(""); setLinkedAOR("");
      loadList();
    } catch (err) {
      setNotice({ kind: "error", text: "Error: " + err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (id) => {
    const res = await fetch("/api/virements/" + id + "/submit", { method: "POST" });
    const data = await res.json();
    if (!res.ok) { setNotice({ kind: "error", text: "Error: " + (data.error || "Failed") }); return; }
    setNotice({ kind: "success", text: data.message + "\nLayers: " + data.layers.join(" → ") });
    loadDetail(id);
    loadList();
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: C.gray500 }}>Loading...</div>;

  // ─── Create Form ───
  if (showForm) {
    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <Btn variant="outline" onClick={() => setShowForm(false)}>← Cancel</Btn>
          <h1 style={{ fontSize: text.h2, fontWeight: 700, margin: 0 }}>New Budget Virement</h1>
        </div>

        {overBudgetAORs.length > 0 && (
          <Card style={{ marginBottom: 16, border: `1px solid ${C.dangerLight}`, background: C.dangerBg }}>
            <div style={{ fontSize: text.md, fontWeight: 700, color: C.danger, marginBottom: 8 }}>⚠ Over-Budget AORs Needing Virement</div>
            {overBudgetAORs.flatMap(aor => shortLines(aor).map(line => (
              <div key={aor.id + line.budgetRef} onClick={() => { setToRef(line.budgetRef); setLinkedAOR(aor.id); setAmount(String(Math.abs(line.after))); }}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", marginBottom: 4, background: C.white, borderRadius: 6, border: `1px solid ${C.gray200}`, cursor: "pointer" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: text.md, fontWeight: 600 }}>{aor.aorNumber} — {aor.description}</div>
                  <div style={{ fontSize: text.xs, color: C.gray500 }}>{line.budgetRef} • Short by {fmt(Math.abs(line.after))}</div>
                </div>
                <Btn variant="outline" style={{ padding: "3px 10px", fontSize: text.xs }}>Use</Btn>
              </div>
            )))}
          </Card>
        )}

        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontSize: text.lg, fontWeight: 700, marginBottom: 14 }}>Transfer Details</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: text.sm, fontWeight: 600, color: C.gray700, display: "block", marginBottom: 4 }} htmlFor="vir-from">From (Donor Budget) *</label>
              <select id="vir-from" value={fromRef} onChange={e => setFromRef(e.target.value)} style={{ width: "100%", padding: "8px 12px", border: `1px solid ${C.gray300}`, borderRadius: 6, fontSize: text.md, fontFamily: "inherit", background: C.white, boxSizing: "border-box" }}>
                <option value="">-- Select donor budget --</option>
                {budgets.filter(b => b.ref !== toRef).map(b => (
                  <option key={b.ref} value={b.ref}>{b.ref} — {b.bu} {b.category} (Avail: {fmt(b.available)})</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: text.sm, fontWeight: 600, color: C.gray700, display: "block", marginBottom: 4 }} htmlFor="vir-to">To (Recipient Budget) *</label>
              <select id="vir-to" value={toRef} onChange={e => setToRef(e.target.value)} style={{ width: "100%", padding: "8px 12px", border: `1px solid ${C.gray300}`, borderRadius: 6, fontSize: text.md, fontFamily: "inherit", background: C.white, boxSizing: "border-box" }}>
                <option value="">-- Select recipient budget --</option>
                {budgets.filter(b => b.ref !== fromRef).map(b => (
                  <option key={b.ref} value={b.ref}>{b.ref} — {b.bu} {b.category}</option>
                ))}
              </select>
            </div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: text.sm, fontWeight: 600, color: C.gray700, display: "block", marginBottom: 4 }} htmlFor="vir-amount">Transfer Amount ($) *</label>
            <input id="vir-amount" type="number" min="1" value={amount} onChange={e => setAmount(e.target.value)} placeholder="e.g. 30000" style={{ width: 200, padding: "8px 12px", border: `1px solid ${C.gray300}`, borderRadius: 6, fontSize: text.lg, fontFamily: "inherit", boxSizing: "border-box" }} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: text.sm, fontWeight: 600, color: C.gray700, display: "block", marginBottom: 4 }} htmlFor="vir-reason">Justification *</label>
            <textarea id="vir-reason" value={reason} onChange={e => setReason(e.target.value)} placeholder="Explain why the budget transfer is needed..." style={{ width: "100%", padding: "8px 12px", border: `1px solid ${C.gray300}`, borderRadius: 6, fontSize: text.md, fontFamily: "inherit", minHeight: 80, resize: "vertical", boxSizing: "border-box" }} />
          </div>
          {/* The linkage is what releases a PENDING VIREMENT AOR when this
              virement is approved — keep it visible, not just panel-set state. */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: text.sm, fontWeight: 600, color: C.gray700, display: "block", marginBottom: 4 }} htmlFor="vir-linked-aor">Linked AOR (released when this virement is approved)</label>
            <select id="vir-linked-aor" value={linkedAOR} onChange={e => setLinkedAOR(e.target.value)} style={{ width: "100%", padding: "8px 12px", border: `1px solid ${C.gray300}`, borderRadius: 6, fontSize: text.md, fontFamily: "inherit", background: C.white, boxSizing: "border-box" }}>
              <option value="">-- None — plain budget transfer --</option>
              {aors.filter(p => p.id === linkedAOR || (p.budgetCheck?.status === "OVER_BUDGET" && !p.linkedVirementId)).map(p => (
                <option key={p.id} value={p.id}>{p.aorNumber} — {p.description} ({shortfallText(p)})</option>
              ))}
            </select>
          </div>
        </Card>

        <Notice notice={notice} onDismiss={() => setNotice(null)} />

        {/* Transfer preview */}
        {fromRef && toRef && amount > 0 && (
          <Card style={{ marginBottom: 16, border: `2px solid ${C.purpleBorder}` }}>
            <div style={{ fontSize: text.lg, fontWeight: 700, color: C.purple, marginBottom: 14 }}>Transfer Preview</div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ flex: 1, padding: 16, background: C.dangerBg, borderRadius: 10, textAlign: "center" }}>
                <div style={{ fontSize: text.xs, color: C.gray500 }}>FROM (Donor)</div>
                <div style={{ fontSize: text.lg, fontWeight: 700, marginTop: 4 }}>{fromRef}</div>
                <div style={{ fontSize: text.sm, color: C.gray600 }}>{fromBudget?.bu}</div>
                <div style={{ fontSize: text.xs, color: C.gray500, marginTop: 8 }}>Annual: {fmt(fromBudget?.annual || 0)} · Avail: {fmt(fromBudget?.available || 0)}</div>
                <div style={{ fontSize: text.md, fontWeight: 700, color: C.danger }}>Annual after: {fmt((fromBudget?.annual || 0) - parseFloat(amount || 0))}</div>
                <div style={{ fontSize: text.h2, fontWeight: 900, color: C.danger, marginTop: 4 }}>-{fmt(parseFloat(amount || 0))}</div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <div style={{ width: 48, height: 48, borderRadius: "50%", background: C.purple, display: "flex", alignItems: "center", justifyContent: "center", color: C.white, fontSize: text.h1 }}>→</div>
                <div style={{ fontSize: text.lg, fontWeight: 800, color: C.purple, marginTop: 4 }}>{fmt(parseFloat(amount || 0))}</div>
              </div>
              <div style={{ flex: 1, padding: 16, background: C.successBg, borderRadius: 10, textAlign: "center" }}>
                <div style={{ fontSize: text.xs, color: C.gray500 }}>TO (Recipient)</div>
                <div style={{ fontSize: text.lg, fontWeight: 700, marginTop: 4 }}>{toRef}</div>
                <div style={{ fontSize: text.sm, color: C.gray600 }}>{toBudget?.bu}</div>
                <div style={{ fontSize: text.xs, color: C.gray500, marginTop: 8 }}>Annual: {fmt(toBudget?.annual || 0)} · Avail: {fmt(toBudget?.available || 0)}</div>
                <div style={{ fontSize: text.md, fontWeight: 700, color: C.success }}>Annual after: {fmt((toBudget?.annual || 0) + parseFloat(amount || 0))}</div>
                <div style={{ fontSize: text.h2, fontWeight: 900, color: C.success, marginTop: 4 }}>+{fmt(parseFloat(amount || 0))}</div>
              </div>
            </div>
          </Card>
        )}

        <div style={{ display: "flex", gap: 10 }}>
          <Btn variant="purple" onClick={handleCreate} disabled={submitting || !fromRef || !toRef || !amount || !reason}>
            {submitting ? "Creating..." : "Create Virement"}
          </Btn>
          <Btn variant="outline" onClick={() => setShowForm(false)}>Cancel</Btn>
        </div>
      </div>
    );
  }

  // ─── Detail View ───
  if (sel && detail) {
    const vir = detail;
    const canSubmit = vir.status === "DRAFT" || vir.status === "RETURNED";

    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <Btn variant="outline" onClick={() => { setSel(null); setDetail(null); }} style={{ padding: "4px 10px", fontSize: text.xs }}>← Back</Btn>
          <h1 style={{ fontSize: text.h2, fontWeight: 700, margin: 0 }}>{vir.virNumber}</h1>
          <Badge status={vir.status} />
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            {canSubmit && <Btn variant="success" onClick={() => handleSubmit(vir.id)}>Submit for Approval</Btn>}
          </div>
        </div>

        <Notice notice={notice} onDismiss={() => setNotice(null)} />

        <ReturnedBanner status={vir.status} approvals={vir.approvals} />

        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, fontSize: text.md }}>
            <div><span style={{ color: C.gray500 }}>Requestor:</span> <strong>{vir.requestor?.displayName}</strong></div>
            <div><span style={{ color: C.gray500 }}>Date:</span> <strong>{new Date(vir.createdAt).toLocaleDateString()}</strong></div>
            <div><span style={{ color: C.gray500 }}>Amount:</span> <strong style={{ fontSize: text.h2, color: C.purple }}>{fmt(vir.amount)}</strong></div>
          </div>
        </Card>

        {/* Transfer visual */}
        <Card style={{ marginBottom: 16, border: `2px solid ${C.purpleBorder}` }}>
          <div style={{ fontSize: text.lg, fontWeight: 700, color: C.purple, marginBottom: 14 }}>Budget Transfer</div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ flex: 1, padding: 16, background: C.dangerBg, borderRadius: 10, textAlign: "center" }}>
              <div style={{ fontSize: text.xs, color: C.gray500 }}>FROM (Donor)</div>
              <div style={{ fontSize: text.lg, fontWeight: 700, marginTop: 4 }}>{vir.fromRef}</div>
              <div style={{ fontSize: text.sm, color: C.gray600 }}>{vir.fromBudget?.bu} — {vir.fromBudget?.category}</div>
              <div style={{ fontSize: text.xs, color: C.gray500, marginTop: 8 }}>Annual: {fmt(vir.fromBudget?.annual || 0)}</div>
              <div style={{ fontSize: text.md, fontWeight: 700, color: C.danger }}>Annual after: {fmt((vir.fromBudget?.annual || 0) - vir.amount)}</div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <div style={{ width: 48, height: 48, borderRadius: "50%", background: C.purple, display: "flex", alignItems: "center", justifyContent: "center", color: C.white, fontSize: text.h1 }}>→</div>
              <div style={{ fontSize: text.lg, fontWeight: 800, color: C.purple, marginTop: 4 }}>{fmt(vir.amount)}</div>
            </div>
            <div style={{ flex: 1, padding: 16, background: C.successBg, borderRadius: 10, textAlign: "center" }}>
              <div style={{ fontSize: text.xs, color: C.gray500 }}>TO (Recipient)</div>
              <div style={{ fontSize: text.lg, fontWeight: 700, marginTop: 4 }}>{vir.toRef}</div>
              <div style={{ fontSize: text.sm, color: C.gray600 }}>{vir.toBudget?.bu} — {vir.toBudget?.category}</div>
              <div style={{ fontSize: text.xs, color: C.gray500, marginTop: 8 }}>Annual: {fmt(vir.toBudget?.annual || 0)}</div>
              <div style={{ fontSize: text.md, fontWeight: 700, color: C.success }}>Annual after: {fmt((vir.toBudget?.annual || 0) + vir.amount)}</div>
            </div>
          </div>
        </Card>

        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontSize: text.lg, fontWeight: 700, marginBottom: 8 }}>Justification</div>
          <div style={{ padding: 14, background: C.gray50, borderRadius: 8, fontSize: text.md, lineHeight: 1.6 }}>{vir.reason}</div>
        </Card>

        {vir.linkedAORs?.length > 0 && (
          <Card style={{ marginBottom: 16, background: C.purpleBg, border: `1px solid ${C.purpleBorder}` }}>
            <div style={{ fontSize: text.md, fontWeight: 700, color: C.purple, marginBottom: 8 }}>Linked Approval of Needs Requests</div>
            {vir.linkedAORs.map(aor => (
              <div key={aor.aorNumber} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0" }}>
                <span style={{ fontWeight: 600, color: C.primary }}>{aor.aorNumber}</span>
                <span>{aor.description}</span>
                <span style={{ marginLeft: "auto", fontWeight: 700 }}>{fmt(aor.total)}</span>
                <Badge status={aor.status} />
              </div>
            ))}
          </Card>
        )}

        {vir.status === "EXECUTED" && (
          <Card style={{ marginBottom: 16, background: C.successBg, border: `1px solid ${C.successLight}` }}>
            <div style={{ fontSize: text.md, fontWeight: 700, color: C.success }}>✅ Virement executed. Budget lines have been updated.</div>
          </Card>
        )}

        <ApprovalTrail approvals={vir.approvals} docType="VIREMENT" />

        <Card>
          <DocumentSection
            entityType="virement"
            entityId={vir.id}
            documents={vir.documents || []}
            onChange={() => loadDetail(vir.id)}
          />
        </Card>

        <WatcherPanel docType="VIREMENT" docId={vir.id} canManage={!!me && (me.id === vir.requestorId || me.roleName === "Administrator")} />
      </div>
    );
  }

  // ─── List View ───
  const pendingVir = virements.filter(v => v.status === "PENDING_APPROVAL");

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: text.h1, fontWeight: 700, color: C.gray1100, margin: 0 }}>Budget Virements</h1>
          <p style={{ fontSize: text.md, color: C.gray500, margin: "2px 0 0" }}>Transfer budget between business units</p>
        </div>
        <Btn variant="purple" onClick={() => { setNotice(null); setShowForm(true); }}>+ New Virement</Btn>
      </div>

      <Notice notice={notice} onDismiss={() => setNotice(null)} />

      {pendingVir.length > 0 && (
        <Card style={{ marginBottom: 16, border: `1px solid ${C.purpleBorder}` }}>
          <div style={{ fontSize: text.lg, fontWeight: 700, color: C.purple, marginBottom: 12 }}>Pending Approval ({pendingVir.length})</div>
          {pendingVir.map(v => (
            <div key={v.id} onClick={() => loadDetail(v.id)} style={{ display: "flex", alignItems: "center", gap: 12, padding: 14, background: C.purpleBg, borderRadius: 8, cursor: "pointer", marginBottom: 8 }}>
              <span style={{ fontSize: text.h2 }}>🔀</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: text.md, fontWeight: 700 }}>{v.virNumber}: {v.fromRef} → {v.toRef}</div>
                <div style={{ fontSize: text.sm, color: C.gray600 }}>{v.requestor?.displayName} {v.linkedAORs?.length > 0 ? `• For: ${v.linkedAORs[0].aorNumber}` : ""}</div>
              </div>
              <div style={{ fontSize: text.h3, fontWeight: 800, color: C.purple }}>{fmt(v.amount)}</div>
              <Badge status={v.status} />
            </div>
          ))}
        </Card>
      )}

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.md }}>
          <thead><tr>{["Virement No.", "Date", "From", "To", "Amount", "Linked AOR", "Requestor", "Status"].map((h, i) => (
            <th scope="col" key={i} style={{ padding: "10px 12px", textAlign: "left", borderBottom: `2px solid ${C.gray200}`, color: C.gray600, fontWeight: 600, fontSize: text.xs, textTransform: "uppercase" }}>{h}</th>
          ))}</tr></thead>
          <tbody>
            {virements.map(v => (
              <tr key={v.id} {...rowAction(() => { setNotice(null); loadDetail(v.id); })} style={{ cursor: "pointer", borderBottom: `1px solid ${C.gray100}` }}
                onMouseEnter={e => e.currentTarget.style.background = C.gray50}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <td style={{ padding: "10px 12px", fontWeight: 600, color: C.purple }}>{v.virNumber}</td>
                <td style={{ padding: "10px 12px" }}>{new Date(v.createdAt).toLocaleDateString()}</td>
                <td style={{ padding: "10px 12px" }}>{v.fromRef}</td>
                <td style={{ padding: "10px 12px" }}>{v.toRef}</td>
                <td style={{ padding: "10px 12px", fontWeight: 700 }}>{fmt(v.amount)}</td>
                <td style={{ padding: "10px 12px", color: C.primary }}>{v.linkedAORs?.[0]?.aorNumber || "—"}</td>
                <td style={{ padding: "10px 12px" }}>{v.requestor?.displayName}</td>
                <td style={{ padding: "10px 12px" }}><Badge status={v.status} /></td>
              </tr>
            ))}
            {virements.length === 0 && (
              <tr><td colSpan={8} style={{ padding: 30, textAlign: "center", color: C.gray500 }}>No virements yet — move budget between lines with + New Virement.</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
